import { Box, CircularProgress, SxProps, Typography } from "@mui/material";
import CircularProgressWithLabel from "components/CircularProgressWithLabel";
import SPINNER_MODE from "enums/SPINNER_MODE";
import STATUS_CODE from "enums/STATUS_CODE";
import { ReactNode } from "react";
import { VidletApiError } from "types/Errors";
import AnimatedButterfly from "./components/AnimatedButterfly";
import SpinnerVerbsIndicator from "./components/SpinnerVerbsIndicator";

interface Props {
  children?: React.ReactNode | undefined;
  error?: VidletApiError;
  percentComplete?: number;
  hideContentsWhileSpinner?: boolean;
  noResultsText?: ReactNode;
  showSpinner?: boolean;
  spinnerMode?: SPINNER_MODE;
  spinnerCaption?: ReactNode;
  sx?: SxProps;
  TransitionalWrapper?: React.ElementType;
}

const LoadingIcon = ({
  percentComplete,
  spinnerMode = SPINNER_MODE.DEFAULT,
}: {
  percentComplete?: number;
  spinnerMode?: SPINNER_MODE;
}) => {
  if (percentComplete != undefined) {
    return (
      <CircularProgressWithLabel sx={{ zIndex: 1 }} value={percentComplete} />
    );
  }

  switch (spinnerMode) {
    case SPINNER_MODE.VERBS:
      return <SpinnerVerbsIndicator />;
    case SPINNER_MODE.BUTTERFLY:
      return <AnimatedButterfly />;
    case SPINNER_MODE.DEFAULT:
      return <CircularProgress sx={{ zIndex: 1 }} />;
    default: {
      const _exhaustive: never = spinnerMode;
      return _exhaustive;
    }
  }
};

const NoResultsContent = ({
  noResultsText,
  defaultStyle,
}: {
  noResultsText?: ReactNode;
  defaultStyle: SxProps;
}) => (
  <Typography
    component="span"
    sx={{
      color: (theme) => theme.palette.grey.A400,
      fontSize: "2rem",
      padding: "1.5rem",
      ...defaultStyle,
    }}
  >
    {noResultsText || "There are no results to display"}
  </Typography>
);

export default function TransitionalRegion({
  children,
  error,
  noResultsText,
  percentComplete,
  showSpinner,
  spinnerMode = SPINNER_MODE.DEFAULT,
  spinnerCaption,
  sx,
  TransitionalWrapper,
}: Props) {
  const defaultStyle: SxProps = {
    flex: 1,
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    textAlign: "center",
    ...sx,
  };

  if (
    error &&
    error.response?.status != STATUS_CODE.FORBIDDEN &&
    error.response?.status != STATUS_CODE.UNAUTHORIZED
  ) {
    return (
      <Typography
        component="span"
        sx={{
          fontSize: "2rem",
          padding: "1.5rem",
          color: "error.main",
          ...defaultStyle,
        }}
      >
        {error?.response?.data?.error?.message ||
          "Something went wrong. Please try again later."}
      </Typography>
    );
  }

  const renderTransitionalContent = () => {
    if (showSpinner) {
      return (
        <Box
          component="span"
          sx={{
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "5rem",
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            ...sx,
          }}
        >
          <LoadingIcon
            percentComplete={percentComplete}
            spinnerMode={spinnerMode}
          />
          {spinnerCaption && (
            <Typography
              component="span"
              sx={{
                mt: 2,
                px: 2,
                color: "text.secondary",
                fontSize: "1rem",
                fontWeight: 500,
                textAlign: "center",
                maxWidth: "28rem",
                zIndex: 1,
              }}
            >
              {spinnerCaption}
            </Typography>
          )}
        </Box>
      );
    }

    if (!children) {
      return (
        <NoResultsContent
          noResultsText={noResultsText}
          defaultStyle={defaultStyle}
        />
      );
    }

    return null;
  };

  const transitionalContent = renderTransitionalContent();

  return (
    <>
      {children}
      {TransitionalWrapper ? (
        <TransitionalWrapper>{transitionalContent}</TransitionalWrapper>
      ) : (
        transitionalContent
      )}
    </>
  );
}
